/**
 * useBranding — surface per-tenant white-label assets (logo + primary color +
 * welcome copy) to the admin shell + login screen.
 *
 * Two facets:
 *   1. `useBranding()` — pure data hook. Reads `Society.brand_*` via the
 *      cached `/society/me` query (or a one-shot fetch if no session yet).
 *      Returns `null` for any field the tenant hasn't customized so callers
 *      can `??` against the HoodDesk default.
 *   2. `useBrandLogoBlobUrl()` — the GET /v1/society/me/logo stream is auth-
 *      gated, so we can't drop a Bearer header into `<img src>`. This hook
 *      fetches the bytes via the axios client and surfaces a temporary
 *      `URL.createObjectURL` value. Re-fetches whenever the `?ts=` cache-bust
 *      changes (which is exactly when a fresh logo gets uploaded).
 *
 * Both are safe to call from any route. The data hook works fine
 * pre-authentication (returns nulls) so the login screen can call it.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, get } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';

export interface BrandingData {
  brand_logo_url: string | null;
  brand_primary_color: string | null;
  brand_welcome_copy: string | null;
}

interface SocietyBrandingRow {
  brand_logo_url?: string | null;
  brand_primary_color?: string | null;
  brand_welcome_copy?: string | null;
}

const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Hook for in-shell consumers. Lives on the same `['society','me']` query
 * key as the Settings page so Settings → Save → AppShell re-render happens
 * via standard react-query invalidation.
 */
export function useBranding(): BrandingData {
  const hasSession = useAuthStore((s) => Boolean(s.accessToken));
  const role = useAuthStore((s) => s.user?.role);
  // Skip the fetch entirely for super-admins — they operate cross-tenant and
  // have no `society_id` claim, so /society/me would 400.
  const enabled = hasSession && role !== 'super_admin' && role !== 'vendor';
  const query = useQuery({
    queryKey: ['society', 'me'],
    queryFn: () => get<SocietyBrandingRow>('/society/me'),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false
  });
  const data = query.data;
  return {
    brand_logo_url: data?.brand_logo_url ?? null,
    brand_primary_color:
      data?.brand_primary_color && HEX_COLOR_RE.test(data.brand_primary_color)
        ? data.brand_primary_color
        : null,
    brand_welcome_copy: data?.brand_welcome_copy ?? null
  };
}

/**
 * Auth-gated blob → object-URL fetcher. Returns `null` until the bytes
 * arrive, and re-runs when `brandLogoUrl` (with its `?ts=…` cache-bust)
 * changes. Revokes the object URL on unmount.
 */
export function useBrandLogoBlobUrl(brandLogoUrl: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!brandLogoUrl) {
      setUrl(null);
      return;
    }
    let revoked = false;
    let createdUrl: string | null = null;
    api
      .get<Blob>('/society/me/logo', { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(res.data);
        setUrl(createdUrl);
      })
      .catch(() => {
        if (!revoked) setUrl(null);
      });
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [brandLogoUrl]);
  return url;
}
